import { isEmpty } from 'lodash';
import { CleanDetails, InitialBookingState } from '../types';
import { formatAddress } from '@/utils/formatAddress';
import { generateArray } from '@/utils/generateArray';
import { Wallet } from '@/features/users/shared/types';

const DASHBOARD_SHORT_HOUR_OPTIONS = [0.5, 1, 1.5];

const normalizeHourOption = (value: number) => Number(value.toFixed(10));

export const getDefaultClientAddress = (
  booking: InitialBookingState
): string | null => {
  let defaultAddress = null;
  if (!isEmpty(booking.client)) {
    const defAddress = booking.client.address.find(
      (address) => address.default
    );
    if (defAddress) {
      const { formattedAddressWithPostcode } = formatAddress(
        defAddress.formatted_address,
        defAddress.postcode
      );
      defaultAddress = formattedAddressWithPostcode;
    }
  }
  return defaultAddress;
};

export const buildDashboardHourOptions = (
  start: number,
  end: number,
  step: number
) => {
  const configuredOptions = generateArray(start, end, step);
  const defaultSelectedOption = normalizeHourOption(start);
  const sortedOptions = [
    ...DASHBOARD_SHORT_HOUR_OPTIONS,
    ...configuredOptions,
  ]
    .map(normalizeHourOption)
    .filter((option, index, options) => options.indexOf(option) === index)
    .sort((a, b) => a - b);

  return sortedOptions.map((numberOfHours, index) => ({
    id: index + 1,
    numberOfHours,
    selected: numberOfHours === defaultSelectedOption,
  }));
};

export const setEvaIcon = (isEmpty: boolean) =>
  isEmpty ? 'eva:plus-fill' : 'eva:edit-fill';

export const buildBookingPricingPayload = (
  cleanDetails: Pick<
    CleanDetails,
    'baseRate' | 'manualBaseRate' | 'manualBaseRateTouched'
  >
) => ({
  price_per_hour: cleanDetails.baseRate,
  ...(cleanDetails.manualBaseRateTouched && cleanDetails.manualBaseRate !== null
    ? { manual_price_per_hour: cleanDetails.manualBaseRate }
    : {}),
});

export const buildWalletLabel = (wallet: Wallet) => {
  const descriptor = wallet.name || wallet.brand || 'Saved card';
  const last4 = wallet.last4 ? `**** ${wallet.last4}` : '';
  const expiry =
    wallet.exp_month && wallet.exp_year
      ? `Exp ${wallet.exp_month}/${wallet.exp_year}`
      : '';
  const defaultLabel = wallet.default ? 'Default' : '';

  return [descriptor, last4, expiry, defaultLabel].filter(Boolean).join(' · ');
};

export const getSelectedPaymentWallet = (
  booking: Pick<InitialBookingState, 'client' | 'paymentMethodId'>
) =>
  booking.client?.wallet?.find(
    (wallet) => wallet.id === booking.paymentMethodId
  );

export const buildBookingPaymentMethodPayload = (
  booking: Pick<InitialBookingState, 'client' | 'paymentMethodId'>
) => {
  const selectedWallet = getSelectedPaymentWallet(booking);

  return selectedWallet
    ? {
        payment_saved_card: selectedWallet.id,
        payment_method: selectedWallet.stripe_id,
      }
    : {};
};
